// The board settings the views read, from juice.json and ui.json render (IMPLEMENTATION §2.9 "Views"): the
// zero-duration mode, chevron / stall-pulse clocks, ghost / paused looks, badge sizes, the input glows. PURE
// mapping so the renderer never hard-codes a number. The event FX (M10) read the full juice table through
// render/juice/config.ts (juiceCfg) and the overlay geometry through geoCfg.
import type { JuiceData, UiData } from '../data/schema.ts';
import type { GlowKind } from './Renderer.ts';

export interface FxConfig {
  /** Zero-duration mode (?test unless ?fx=1): no tweens, no FX, no idle animation. */
  instant: boolean;
  /** juice.json easing.out (the expansion camera tween). */
  easeOut: string;
  /** ui.json sprites.itemScaleMin: a spawn / placement grows from it. */
  spawnScale: number;
  /** juice.json targets.byAmmoTier (target kind per ammo tier). */
  byAmmoTier: readonly string[];
  chevronPhases: number;
  chevronCycleMs: number;
  stallPulseHz: number;
  ghostAlpha: number;
  pausedAlpha: number;
  chevronSize: number;
  /** An inserted item shrinks to this display size (cells). */
  insertEndScale: number;
  /** Error stall badges (and pulsing glows) pulse between this alpha and 1. */
  stallPulseMin: number;
  /** Stall badge share of the cell, and its minimum in CSS px (§2.6 "36% (≥ 18 px)"; M8 merge). */
  stallBadge: number;
  stallBadgeMinPx: number;
  /** Multiply tint of a paused building (palette `stall.paused`). */
  pausedTint: number;
  expansionTweenMs: number;
  eventQueueMax: number;
  /** M6 input overlay: fill colour per glow kind (palette), the faint floor / home alphas, the pulse. */
  glow: Readonly<Record<GlowKind, number>>;
  glowFloorAlpha: number;
  glowHomeAlpha: number;
  /** juice.json events.select.pulseHz (merge-partner and selection glows). */
  glowPulseHz: number;
}

export function fxConfig(
  j: JuiceData,
  ui: UiData,
  palette: Readonly<Record<string, string>>,
  instant: boolean,
): FxConfig {
  return {
    instant,
    easeOut: j.easing.out,
    spawnScale: ui.sprites.itemScaleMin,
    byAmmoTier: [...j.targets.byAmmoTier],
    chevronPhases: ui.render.chevronPhases,
    chevronCycleMs: ui.render.chevronCycleMs,
    stallPulseHz: ui.render.stallPulseHz,
    ghostAlpha: ui.render.ghostAlpha,
    pausedAlpha: ui.render.pausedAlpha,
    chevronSize: ui.sprites.chevronSize,
    insertEndScale: ui.render.insertEndScale,
    stallPulseMin: ui.render.stallPulseMin,
    stallBadge: ui.sprites.stallBadge,
    stallBadgeMinPx: ui.sprites.stallBadgeMinPx,
    pausedTint: hexToInt(palette['stall.paused'] ?? '#ffffff'),
    expansionTweenMs: instant ? 0 : ui.render.expansionTweenMs,
    eventQueueMax: ui.render.eventQueueMax,
    glow: {
      merge: hexToInt(palette['glow.merge'] ?? '#ffffff'),
      valid: hexToInt(palette['glow.valid'] ?? '#ffffff'),
      floor: hexToInt(palette['ui.cream'] ?? '#ffffff'),
      select: hexToInt(palette['glow.select'] ?? '#ffffff'),
      home: hexToInt(palette['ui.cream2'] ?? '#ffffff'),
      path: hexToInt(palette['glow.select'] ?? '#ffffff'),
      pathBad: hexToInt(palette['glow.invalid'] ?? '#ffffff'),
    },
    glowFloorAlpha: ui.input.glowFloorAlpha,
    glowHomeAlpha: ui.input.glowHomeAlpha,
    glowPulseHz: j.events.select.pulseHz,
  };
}

/** '#rrggbb' → 0xrrggbb. */
export function hexToInt(hex: string): number {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return Number.isFinite(n) ? n : 0xffffff;
}
